/**
 * pieceProfileResult — the ONE interpretation of what PieceProfileModal just returned.
 *
 * P3.4. The modal is wired from three doors — StockInForm (the submit conversation),
 * InventoryOverview (onboarding's opening batch) and SalesReport (the line-item edit) —
 * and each had its own hand-written `onSave` that read `data.corrected_quantity` and
 * decided for itself what to do with it. Three copies of one rule is three chances for
 * one of them to drift, and the class of bug it produces is the worst kind this module
 * can have: a quantity silently multiplied by a pack size when it should not have been,
 * creating stock that does not physically exist.
 *
 * So the interpretation lives here, once, and every door calls it.
 *
 * THE RULE, in full:
 *   `corrected_quantity` is present ONLY when the person explicitly answered "the number
 *   I typed was packets". It is ABSENT — not equal to the current quantity, absent — when
 *   they answered "it was already pieces", so that "no correction" and "a correction that
 *   happens to match" stay distinguishable. A door that treats a missing value as zero, or
 *   that multiplies on its own initiative, is reintroducing the bug.
 *
 *   The TOTAL is never touched. It is what the bill says was paid, and a corrected COUNT
 *   changes the price per piece, never the money. That is the same "total wins" rule the
 *   backend gate uses to resolve the canonical triple.
 */

/**
 * @param {object} data       exactly what PieceProfileModal's onSave was handed
 * @param {object} row        { quantity, total_price?, unit_price? } — the row as it stands
 * @returns {{
 *   quantity: number,        the count the row should now carry
 *   unitPrice: number|null,  the per-piece price that follows from it, or null
 *   corrected: boolean,      whether the count actually changed
 *   meaning: 'packets'|'pieces',
 * }}
 */
export function applyPieceProfileResult(data, row = {}) {
  const currentQty = Number(row.quantity);
  const total = Number(row.total_price != null ? row.total_price : data?.entered_total);

  const corrected = Number(data?.corrected_quantity);
  const hasCorrection = Number.isFinite(corrected) && corrected > 0;

  // `quantity_meaning` is the explicit answer; `corrected_quantity`'s presence is the
  // same fact stated structurally. Either one alone is enough, and they cannot disagree
  // because the modal derives both from the same choice.
  const meaning = data?.quantity_meaning === 'packets' || hasCorrection ? 'packets' : 'pieces';

  const quantity = meaning === 'packets' && hasCorrection
    ? corrected
    : (Number.isFinite(currentQty) ? currentQty : corrected);

  const unitPrice = Number.isFinite(total) && total > 0 && Number.isFinite(quantity) && quantity > 0
    ? total / quantity
    : null;

  return {
    quantity,
    unitPrice,
    corrected: Number.isFinite(currentQty) ? quantity !== currentQty : hasCorrection,
    meaning,
  };
}

export default applyPieceProfileResult;
